import React from 'react';

// Helper function to ensure URLs are absolute
const ensureAbsoluteUrl = (url: string, baseUrl: string = 'https://goodcart.org'): string => {
  // If the URL already starts with http:// or https://, it's already absolute
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If the URL starts with a slash, append it to the base URL
  if (url.startsWith('/')) {
    return `${baseUrl}${url}`;
  }
  
  // Otherwise, assume it's a relative path and add a slash
  return `${baseUrl}/${url}`;
};

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  url?: string;
  type?: string;
  locale?: string;
  siteName?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Shop the good stuff, faster',
  description = 'GoodCart helps you shop smarter with vetted, health-conscious products and clean ingredients.',
  image = '/og-image.png',
  imageAlt = 'GoodCart - Shop the good stuff, faster',
  url = 'https://goodcart.org',
  type = 'website',
  locale = 'en_US',
  siteName = 'GoodCart',
  publishedTime,
  modifiedTime,
  author = 'GoodCart Team'
}) => {
  const siteTitle = `GoodCart | ${title}`;
  
  // Update document title
  React.useEffect(() => {
    document.title = siteTitle;

    // Ensure image URL is absolute
    const absoluteImageUrl = ensureAbsoluteUrl(image);

    // Update meta tags
    updateMetaTag('description', description);

    // Update Open Graph meta tags
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:url', url, 'property');
    updateMetaTag('og:title', siteTitle, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', absoluteImageUrl, 'property');
    updateMetaTag('og:image:alt', imageAlt, 'property');
    updateMetaTag('og:locale', locale, 'property');
    updateMetaTag('og:site_name', siteName, 'property');
    
    // Add article specific meta tags if type is article
    if (type === 'article') {
      if (publishedTime) updateMetaTag('article:published_time', publishedTime, 'property');
      if (modifiedTime) updateMetaTag('article:modified_time', modifiedTime, 'property');
      if (author) updateMetaTag('article:author', author, 'property');
    }
    
    // Update Twitter meta tags
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:url', url, 'name');
    updateMetaTag('twitter:title', siteTitle, 'name');
    updateMetaTag('twitter:description', description, 'name');
    updateMetaTag('twitter:image', absoluteImageUrl, 'name');
    updateMetaTag('twitter:image:alt', imageAlt, 'name');
    updateMetaTag('twitter:site', '@goodcartorg', 'name');
    updateMetaTag('twitter:creator', '@goodcartorg', 'name');
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', url);
    
    // Cleanup function
    return () => {
      document.title = 'GoodCart | Shop the good stuff, faster';
    };
  }, [title, description, image, imageAlt, url, type, siteTitle, locale, siteName, publishedTime, modifiedTime, author]);
  
  // Helper function to update meta tags
  const updateMetaTag = (name: string, content: string, attributeName: string = 'name') => {
    let metaTag = document.querySelector(`meta[${attributeName}="${name}"]`);
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(attributeName, name);
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', content);
  };
  
  // This component doesn't render anything visible
  return null;
};

export default SEO;